import {
    useState
} from 'react';
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import {
    Loader2, Clock
} from 'lucide-react';

export function ApiResponse({
    response, isLoading
}) {
    const [activeTab, setActiveTab] = useState('body');

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12 border rounded-md">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Sending request...</span>
            </div>
        );
    }

    if (!response) {
        return (
            <div className="border border-dashed rounded-md p-8 text-center">
                <p className="text-muted-foreground">No response yet</p>
                <p className="text-xs text-muted-foreground mt-2">Test an API to see the response here</p>
            </div>
        );
    }

    if (response.error) {
        return (
            <div className="border border-destructive rounded-md p-6">
                <h3 className="text-lg font-semibold text-destructive mb-2">Error</h3>
                <p className="text-muted-foreground">{response.message}</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <div
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                        response.status >= 200 && response.status < 300
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
                            : response.status >= 400
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100'
                    }`}
                >
                    {response.status}
                </div>
                <span className="text-sm font-medium">{response.statusText}</span>

                {response.responseTime && (
                    <div className="flex items-center text-sm text-muted-foreground ml-auto">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>{response.responseTime} ms</span>
                    </div>
                )}
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="body">Response Body</TabsTrigger>
                    <TabsTrigger value="headers">Headers</TabsTrigger>
                </TabsList>

                <TabsContent value="body" className="mt-4">
                    <div className="border rounded-md p-4 bg-muted/50 overflow-auto max-h-[500px]">
                        <pre className="font-mono text-sm whitespace-pre-wrap">
                            {typeof response.body === 'object' ? JSON.stringify(response.body, null, 2) : response.body}
                        </pre>
                    </div>
                </TabsContent>

                <TabsContent value="headers" className="mt-4">
                    <div className="border rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted">
                                <tr>
                                    <th className="text-left p-2 font-medium">Name</th>
                                    <th className="text-left p-2 font-medium">Value</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {response.headers && (
                                    Object.entries(response.headers).map(([key, value]) => (
                                        <tr key={key}>
                                            <td className="p-2 font-mono">{key}</td>
                                            <td className="p-2 font-mono truncate max-w-[300px]">{value}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
